/**
 * Classe de mapping du tri des listes
 */
export class Sorting {

    /**
     * Constructeur
     * @param columns objets bruts représentant les colonnes triables
     * @param defaultOrder Clé de tri pré-existante
     * @param search (optionnel) type de recherche, commence_par par défaut
     */
    constructor(columns: { key: string, title: string }[],defaultOrder: string,search?: SearchType) {
        //Initialisation
        this.columns = [];

        //Boucle
        for (const column of columns) {
            //Protection
            if (column.key && column.title) {
                //Insertion
                this.columns.push(new Colonne(column.key,column.title));
            }
        }

        //Sauvegarde de la clé de tri par défaut
        this.backupDefaultOrder = defaultOrder;

        //Traitement de la clé de tri pré-existante
        this.parseExistingOrder(defaultOrder);

        //Type de recherche
        if (search) { this.search = search; }
    }

    /** Colonnes triables */
    columns: Colonne[];

    /** Clés de tri */
    key1: CleDeTri = new CleDeTri(undefined);
    key2: CleDeTri = new CleDeTri(undefined);
    key3: CleDeTri = new CleDeTri(undefined);

    /** Mapping */
    private readonly keys: Map<string,CleDeTri[]> = new Map([
        ['key1',[this.key2,this.key3]],
        ['key2',[this.key1,this.key3]],
        ['key3',[this.key1,this.key2]]
    ]);

    /** Type de recherche */
    search: SearchType = SearchType.STARTS_WITH;

    /** Sauvegarde de la clé de tri par défaut */
    private readonly backupDefaultOrder: string;

    /**
     * Retourne la liste des colonnes sélectionnables pour une clé donnée
     * @param key clé concernée
     */
    getSelectList(key: 'key1'|'key2'|'key3'): Colonne[] {
        //Contrôle
        if (!key) { throw new Error("Clé obligatoire"); }

        //Retour après filtrage
        return this.columns.filter(c => {
            const keyA = this.keys.get(key)[0];
            const keyB = this.keys.get(key)[1];
            if (keyA?.colonne?.key == c.key) { return false; }
            if (keyB?.colonne?.key == c.key) { return false; }
            return true;
        });
    }

    /**
     * Retourne la version formatée du tri à appliquer
     */
    getFormattedSorting(): string {
        //Liste des clés formatée
        const formatted: string = Sorting.getSortingAsFormattedString([this.key1,this.key2,this.key3]);

        //Retour
        return formatted != '' ? formatted : this.backupDefaultOrder;
    }

    /**
     * Parse la clé de tri pré-existante
     * @param existingOrder (optionnel) clé de tri pré-existante
     */
    parseExistingOrder(existingOrder?: string): void {
        if (existingOrder && existingOrder != '') {
            //Initialisation
            let isDesc: boolean;
            let theSplit: string;
            let colonne: Colonne;
            this.key1.colonne = undefined;
            this.key2.colonne = undefined;
            this.key3.colonne = undefined;
            this.key1.tri = Ordre.ASC;
            this.key2.tri = Ordre.ASC;
            this.key3.tri = Ordre.ASC;

            //Découpage
            const split: string[] = existingOrder.split(',');

            //Clé 1
            if (split.length) {
                isDesc = split[0].includes('-');
                theSplit = split[0].replace('-','');
                colonne = this.columns.find(c => c.key == theSplit);
                this.updateKeyAndTri(colonne,isDesc);
            }

            //Clé 2
            if (split.length > 1) {
                isDesc = split[1].includes('-');
                theSplit = split[1].replace('-','');
                colonne = this.columns.find(c => c.key == theSplit);
                this.updateKeyAndTri(colonne,isDesc);
            }

            //Clé 3
            if (split.length > 2) {
                isDesc = split[2].includes('-');
                theSplit = split[2].replace('-','');
                colonne = this.columns.find(c => c.key == theSplit);
                this.updateKeyAndTri(colonne,isDesc);
            }
        }
    }

    /**
     * Met à jour la première colonne qui n'est pas affectée
     */
    updateKeyAndTri(colonne: Colonne,isDesc: boolean): void {
        //Si la première clé (key1) n'a pas encore de colonne assignée
        if (this.key1.colonne == null) {
            //On assigne la colonne passée en paramètre à key1
            this.key1.colonne = colonne;
            //Si un tri descendant est demandé, on l'indique pour key1
            if (isDesc) {
                this.key1.tri = Ordre.DESC;
            }
        } else if (this.key2.colonne == null) {
            //Sinon, si la deuxième clé (key2) n'a pas encore de colonne assignée
            //On assigne la colonne à key2
            this.key2.colonne = colonne;
            //Si un tri descendant est demandé, on l'indique pour key2
            if (isDesc) {
                this.key2.tri = Ordre.DESC;
            }
        } else {
            //Sinon, on utilise la troisième clé (key3) par défaut
            //On assigne la colonne à key3
            this.key3.colonne = colonne;
            //Si un tri descendant est demandé, on l'indique pour key3
            if (isDesc) {
                this.key3.tri = Ordre.DESC;
            }
        }
    }

    /**
     * Inversion du mode de recherche
     */
    toggleSearch(): void {
        if (!this.search || this.search == SearchType.STARTS_WITH) {
            this.search = SearchType.CONTAINS;
        } else {
            this.search = SearchType.STARTS_WITH;
        }
    }

    /**
     * Reset du tri par défaut
     */
    reset(): string {
        //Reset de l'objet
        this.parseExistingOrder(this.backupDefaultOrder);

        //Retour
        return this.backupDefaultOrder;
    }

    /**
     * Retourne la version formatée d'une liste de clés
     */
    static getSortingAsFormattedString(keys: Array<CleDeTri>): string {
        return keys
            .filter(key => !!key?.colonne?.key)
            .map(key => (key.tri == Ordre.DESC ? '-' : '') + key.colonne.key)
            .join(',');
    }
}

/**
 * Classe de colonne
 */
export class Colonne {
    /**
     * Constructeur
     * @param key Clé
     * @param title Intitulé
     */
    constructor(key: string, title: string) {
        this.key = key;
        this.title = title;
    }

    //Valeurs
    key: string;
    title: string;
}

/**
 * Classe de clé de tri
 */
export class CleDeTri {

    /**
     * Constructeur
     * @param colonne Colonne concernée
     * @param ordre Ordre du tri (ASC par défaut)
     */
    constructor(colonne: Colonne,ordre: Ordre = Ordre.ASC) {
        this.colonne = colonne;
        this.tri = ordre;
    }

    //Valeurs
    colonne: Colonne;
    tri: Ordre;

    /**
     * Inversion de l'ordre du tri
     */
    toggle(): void {
        if (!this.tri || this.tri == Ordre.ASC) {
            this.tri = Ordre.DESC;
        } else {
            this.tri = Ordre.ASC;
        } 
    }
}

/**
 * Enum d'ordre de tri
 */
export enum Ordre {
    ASC = 'ASC',
    DESC = 'DESC'
}

/**
 * Enum de type de recherche
 */
export enum SearchType {
    STARTS_WITH = 'STARTS_WITH',
    CONTAINS = 'CONTAINS'
}
